import React, { useState } from "react";
import {
    Button,
    Box,
    Typography,
    Divider,
    Link,
    CircularProgress,
    Card,
    CardContent,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import Papa from "papaparse";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import ModalWrapper from "../ModalWrapper/ModalWrapper";
import { useSite } from "../../context/siteContext";
import formatCurrency from "../../utilities/currency";
import { ASSETS_BASE_URL } from "../../config";
import Datagrid from "../Datagrid/Datagrid";
import { useCart } from "../../context/cartContext";
import { useAuth } from "../../context/authContext";
import { Download, FileUpload } from "@mui/icons-material";
import { resolveUploadProducts } from "../../services/scheduleOrderService";
import { saveRequestListBulk } from "../../services/rsplService";  

const countProducts = (items = []) =>
    items.reduce((total, item) => {
        const quantity = Number(item?.Quantity ?? item?.quantity ?? 0);
        return total + (Number.isFinite(quantity) && quantity > 0 ? quantity : 0);
    }, 0);

const normalizeHeaderKey = (key = "") =>
    String(key)
        .trim()
        .toLowerCase()
        .replace(/[\s\-\/]+/g, "_")
        .replace(/[^a-z0-9_]/g, "");

const HEADER_ALIASES = {
    part_number: new Set([
        "part_number",
        "partnumber",
        "part_no",
        "partno",
        "product_number",
        "productnumber",
        "item_number",
        "itemnumber",
        "material_number",
        "materialnumber",
        "sku",
    ]),
    quantity: new Set([
        "quantity",
        "qty",
        "qnty",
        "order_qty",
        "orderquantity",
        "qty_ordered",
    ]),
};

const resolveCanonicalHeader = (key = "") => {
    const normalized = normalizeHeaderKey(key);
    if (HEADER_ALIASES.part_number.has(normalized)) return "part_number";
    if (HEADER_ALIASES.quantity.has(normalized)) return "quantity";
    return normalized;
};

const normalizeRowStrings = (row = {}) => {
    if (!row || typeof row !== "object") return {};
    return Object.entries(row).reduce((acc, [key, value]) => {
        const normalizedKey = resolveCanonicalHeader(key);
        let normalizedValue = value;
        if (typeof normalizedValue === "string") {
            normalizedValue = normalizedValue.trim();
        }
        // Keep first non-empty value when multiple aliases map to same canonical key.
        if (
            acc[normalizedKey] === undefined ||
            acc[normalizedKey] === null ||
            acc[normalizedKey] === ""
        ) {
            acc[normalizedKey] = normalizedValue;
        }
        return acc;
    }, {});
};

const aggregateRowsByPartNumber = (rows = []) => {
    const aggregated = new Map();

    rows.forEach((row) => {
        const partNumber = row?.part_number?.toString().trim();
        const quantity = Number(row?.quantity);
        if (!partNumber || !Number.isFinite(quantity)) return;

        const key = partNumber.toLowerCase();
        const existing = aggregated.get(key);
        if (existing) {
            existing.quantity += quantity;
            return;
        }

        aggregated.set(key, {
            ...row,
            part_number: partNumber,
            quantity,
        });
    });

    return Array.from(aggregated.values());
};

const normalizePartNumber = (value) => String(value || "").trim().toLowerCase();

const getProductMatchNumbers = (product = {}) => [
    product.product_number,
    product.pnum,
    product.alternative_item_number,
    product.alternativeitemnumber,
    product.matched_part_number,
    product.replacement?.old_product_number,
    product.replaced_by?.replaced_product_number,
].map(normalizePartNumber).filter(Boolean);

const buildProductLookup = (products = []) => {
    const lookup = new Map();
    products.forEach((product) => {
        getProductMatchNumbers(product).forEach((partNumber) => {
            if (!lookup.has(partNumber)) lookup.set(partNumber, product);
        });
    });
    return lookup;
};

const CartUpload = ({ onSummary }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [fileName, setFileName] = useState("");
    const [fileProcessing, setFileProcessing] = useState(false);
    const [purchasableParts, setPurchasableParts] = useState([]);
    const [nonPurchasableParts, setNonPurchasableParts] = useState([]);
    const [notAvailableParts, setNotAvailableParts] = useState([]);
    const { selectedSite, selectedSiteCurrency } = useSite();
    const { addMultipleToCart } = useCart();
    const { user } = useAuth();
    const navigate = useNavigate();

    // --- Clean and validate data ---
    const cleanData = (data) =>
        data
            .map(normalizeRowStrings)
            .filter(
                (row) =>
                    row &&
                    Object.values(row).some(
                        (val) => val && val.toString().trim().length > 0
                    ) &&
                    row.part_number &&
                    row.quantity
            );

    // --- Render Part Name with image & link ---
    const renderPartName = (params) => {
        const defaultImage = `${ASSETS_BASE_URL}/stock.png`;
        const src = (() => {
            const raw = params.row?.asset_data?.images?.[0];
            if (raw) {
                if (/^(https?:)?\/\//.test(raw) || raw.startsWith("data:")) return raw;
                const path = raw.startsWith("/") ? raw : `/${raw}`;
                return `${ASSETS_BASE_URL}${path}`;
            }
            return `${ASSETS_BASE_URL}/ProductImages/TMB/${params.row.product_number}.JPG`;
        })();

        const handleError = (e) => {
            e.target.onerror = null;
            e.target.src = defaultImage;
        };

        return (
            <div style={{ display: "flex", alignItems: "center", gap: "8px", justifyContent: "flex-start" }}>
                <img
                    src={src}
                    alt={params.row.part_name}
                    onError={handleError}
                    style={{
                        width: "40px",
                        height: "40px",
                        objectFit: "cover",
                        borderRadius: "4px",
                        flexShrink: 0,
                    }}
                />
                <div>
                    <Link
                        noWrap
                        sx={{
                            cursor: "pointer",
                            flexGrow: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                        variant="body2"
                        onClick={() =>
                            navigate(`/machines/${params.row.machine_id}/${params.row.product_id}`)
                        }
                    >
                        {params.row.part_name}
                    </Link>
                </div>
            </div>
        );
    };

    // --- Handle File Upload ---
    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;
        if (fileProcessing) return;
        setFileProcessing(true);
        setFileName(file.name);

        const fileType = file.name.split(".").pop().toLowerCase();

        const handleDataValidation = async (rawData) => {
            const data = cleanData(rawData);
            if (!data.length) {
                Swal.fire(t('UI_TEXT.EMPTY_FILE_1'), t('UI_TEXT.THE_UPLOADED_FILE_HAS_NO_VALID_DATA'), "error");
                return;
            }

            const normalizedData = aggregateRowsByPartNumber(
                data
                .map((row) => {
                    const partNumber = row.part_number?.toString().trim();
                    const quantityRaw =
                        typeof row.quantity === "string" ? row.quantity.trim() : row.quantity;
                    const parsedQuantity =
                        quantityRaw === "" || quantityRaw === undefined || quantityRaw === null
                            ? ""
                            : Number(quantityRaw);
                    const normalizedQuantity = Number.isFinite(parsedQuantity) ? parsedQuantity : "";

                    return {
                        ...row,
                        part_number: partNumber,
                        quantity: normalizedQuantity,
                    };
                })
                .filter(
                    (row) =>
                        row.part_number &&
                        row.part_number.length > 0 &&
                        row.quantity !== "" &&
                        row.quantity !== null &&
                        row.quantity !== undefined
                )
            );

            if (!normalizedData.length) {
                Swal.fire(t('UI_TEXT.INVALID_DATA'), t('UI_TEXT.THE_UPLOADED_FILE_HAS_NO_USABLE_PART_NUMBERS'), "error");
                return;
            }

            const headers = Object.keys(normalizedData[0]).map((h) => normalizeHeaderKey(h));
            const requiredHeaders = ["part_number", "quantity"];
            const missing = requiredHeaders.filter(
                (header) => !headers.includes(header)
            );

            if (missing.length > 0) {
                Swal.fire(
                    t('UI_TEXT.INVALID_HEADERS'),
                    `${t('UI_TEXT.MISSING_HEADERS')} ${missing.join(", ")}`,
                    "error"
                );
                return;
            }

            try {
                const response = await resolveUploadProducts(
                    selectedSite,
                    normalizedData.map((item) => item.part_number)
                );
                const siteParts = response?.data || [];
                const sitePartsByNumber = buildProductLookup(siteParts);

                // --- Map data ---
                const mappedData = normalizedData.map((item) => {
                    const csvPartNumber = normalizePartNumber(item.part_number);
                    const match = sitePartsByNumber.get(csvPartNumber);

                    if (!match) {
                        return {
                            ...item,
                            part_name: "Not Found",
                            notAvailable: true,
                            purchasable: false,
                            part_price: 0,
                            sub_total: 0,
                        };
                    }

                    const key = selectedSiteCurrency
                        ? `price_${selectedSiteCurrency.toLowerCase()}`
                        : "";
                    const price =
                        match.purchasable === 1 ? Number(match[key]) || 0 : 0;

                    return {
                        ...item,
                        product_id: match.product_id,
                        machine_id: match.machine_id,
                        product_number: match.product_number,
                        part_name: match.product_name || "N/A",
                        part_price: price,
                        purchasable: match.purchasable === 1,
                        notAvailable: false,
                        quantity: Number(item.quantity) || 0,
                        sub_total:
                            price && item.quantity
                                ? Number(price) * Number(item.quantity)
                                : 0,
                    };
                });

                // --- Categorize ---
                const purchasable = mappedData.filter(
                    (i) => i.purchasable && !i.notAvailable
                );
                const nonPurchasable = mappedData.filter(
                    (i) => !i.purchasable && !i.notAvailable
                );
                const notAvailable = mappedData.filter((i) => i.notAvailable);

                setPurchasableParts(purchasable);
                setNonPurchasableParts(nonPurchasable);
                setNotAvailableParts(notAvailable);
                setOpen(true);
            } catch (err) {
                console.error(err);
                Swal.fire(t('GENERAL.ERROR'), t('FILE_UPLOAD.FAILED_TO_FETCH_PRODUCT_INFO'), "error");
            }
        };

        try {
            // --- Parse file ---
            if (fileType === "csv") {
                const csvRows = await new Promise((resolve, reject) => {
                    Papa.parse(file, {
                        header: true,
                        skipEmptyLines: true,
                        complete: (results) => resolve(results.data || []),
                        error: reject,
                    });
                });
                await handleDataValidation(csvRows);
            } else if (fileType === "xlsx") {
                const xlsxRows = await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = (e) => {
                        try {
                            const workbook = XLSX.read(e.target.result, { type: "array" });
                            const sheet = workbook.SheetNames[0];
                            const jsonData = XLSX.utils.sheet_to_json(workbook.Sheets[sheet], {
                                defval: "",
                                raw: false,
                            });
                            resolve(jsonData || []);
                        } catch (error) {
                            reject(error);
                        }
                    };
                    reader.onerror = reject;
                    reader.readAsArrayBuffer(file);
                });
                await handleDataValidation(xlsxRows);
            } else {
                Swal.fire(t('FILE_UPLOAD.INVALID_FILE'), t('FILE_UPLOAD.ONLY_CSV_OR_XLSX_FILES_ARE_ALLOWED'), "error");
            }
        } catch (parseError) {
            console.error(parseError);
            if (fileType === "csv") {
                Swal.fire(t('GENERAL.ERROR'), t('FILE_UPLOAD.FAILED_TO_PARSE_CSV_FILE'), "error");
            } else if (fileType === "xlsx") {
                Swal.fire(t('GENERAL.ERROR'), t('FILE_UPLOAD.FAILED_TO_PARSE_XLSX_FILE'), "error");
            } else {
                Swal.fire(t('GENERAL.ERROR'), t('UI_TEXT.ERROR_PROCESSING_UPLOADED_FILE_PLEASE_TRY_AGAIN'), "error");
            }
        } finally {
            setFileProcessing(false);
            event.target.value = "";
        }
    };

    const handleButtonClick = () =>
        document.getElementById("fileInput").click();

    const handleCancel = () => setOpen(false);

	const handleAddToRequest = async (arg, { successMessage =  t('GENERAL.PARTS_ADDED_TO_REQUEST_LIST') } = {}) => {
        try {
            const reqBody = { products: arg };
            const response = await saveRequestListBulk(arg);
            if (response.status) {
                if (successMessage) {
                    Swal.fire(t('ALERT_MESSAGES.SUCCESS'), successMessage, "success");
                }
                return true;
            }
        } catch (error) {
            console.error("Error adding to Request list:", error);
            Swal.fire("Error", "Failed to add parts to Request list.", "error");
        }
        return false;
    };

	const handleUploadParts = async () => {
        try {
            const customerCareRequestMessage = "Some of the selected parts need up to date pricing, do you want to add them to customer care requests?";
            const purchasableProducts = [];
            const nonPurchasableProducts = [];

            // --- Separate parts into purchasable / non-purchasable ---
            purchasableParts.forEach((product) => {
                const productEntry = {
                    ProductID: product.product_id,
                    MachineID: product.machine_id,
                    Quantity: Number(product.quantity) || 1,
                    Site: selectedSite,
                };
                purchasableProducts.push(productEntry);
            });

            nonPurchasableParts.forEach((product) => {
                const productEntry = {
                    machine_id: product.machine_id,
                    product_id: product.product_id,
                    site_number: selectedSite,
                    user_id: user?.user_id,
                    quantity: Number(product.quantity) || 1,
                };
                nonPurchasableProducts.push(productEntry);
            });

            const hasPurchasable = purchasableProducts.length > 0;
            const hasNonPurchasable = nonPurchasableProducts.length > 0;

            // --- Case 1: Only Non-Purchasable Parts ---
			if (hasNonPurchasable && !hasPurchasable) {
                Swal.fire({
                    icon: "info",
                    title: "Add to Request List?",
                    text: customerCareRequestMessage,
                    showCancelButton: true,
                    confirmButtonText: "Yes, Add to Request List",
                    cancelButtonText: t('BUTTON.CANCEL'),
                }).then(async (result) => {
                    if (result.isConfirmed) {
                        const success = await handleAddToRequest(nonPurchasableProducts);
                        if (success) {
                            setOpen(false);
                            navigate("/requests");
                        }
                    }
                });
                return;
            }

            // --- Case 2: Both Non-Purchasable and Purchasable ---
			if (hasNonPurchasable && hasPurchasable) {
                Swal.fire({
                    icon: "info",
                    title: t('CART.ADD_PARTS_TO_CART_AND_REQUEST_LIST'),
                    text: customerCareRequestMessage,
                    showCancelButton: true,
                    showDenyButton: true,
                    confirmButtonText: t('CART.YES_ADD_BOTH'),
                    denyButtonText: t('CART.NO_JUST_ADD_TO_CART'),
                    cancelButtonText: t('BUTTON.CANCEL'),
                }).then(async (result) => {
                    if (result.isConfirmed) {
                        // User clicked "Yes, Add Both"
                        const addedToRequests = await handleAddToRequest(nonPurchasableProducts, { successMessage: null });
                        if (addedToRequests) {
                            await addMultipleToCart(purchasableProducts);
                            const cartCount = countProducts(purchasableProducts);
                            const requestCount = countProducts(nonPurchasableProducts);
                            if (cartCount && requestCount && typeof onSummary === "function") {
                                onSummary({ cartCount, requestCount });
                            }
                            Swal.fire(t('ALERT_MESSAGES.SUCCESS'), t('GENERAL.PARTS_ADDED_TO_CART_AND_REQUEST_LIST'), "success");
                            setOpen(false);
                        }
                    } else if (result.isDenied) {
                        // User clicked "No, Just Add to Cart"
                        await addMultipleToCart(purchasableProducts);
                        Swal.fire(t('ALERT_MESSAGES.SUCCESS'), t('UI_TEXT.PURCHASABLE_PARTS_ADDED_TO_CART'), "success");
                        setOpen(false);
                    }
                    // If result.isDismissed → user clicked "Cancel"
                });
                return;
            }


            // --- Case 3: Only Purchasable Parts ---
            if (!hasNonPurchasable && hasPurchasable) {
                await addMultipleToCart(purchasableProducts);
                Swal.fire(t('ALERT_MESSAGES.SUCCESS'), t('UI_TEXT.PURCHASABLE_PARTS_ADDED_TO_CART'), "success");
                setOpen(false);
                return;
            }

            // --- Case 4: No Parts Available ---
            if (!hasNonPurchasable && !hasPurchasable) {
                Swal.fire("No Parts", t('UI_TEXT.NO_VALID_PARTS_AVAILABLE_TO_ADD_TO_CART_OR_REQUEST_LIST'), "info");
                return;
            }
        } catch (error) {
            console.error("Error while uploading parts:", error);
            Swal.fire("Error", t('UI_TEXT.FAILED_TO_UPLOAD_PARTS'), "error");
        }
    };

    const renderQuantity = (params) => (
        <Box sx={{ display: "flex", height: '100%', width: '100%', justifyContent: "center", alignItems: "center" }}>
            <Typography variant="body2" color="text.secondary">x</Typography>
            <Typography variant="body1" sx={{ ml: 0.5 }}>{params.value ?? 0}</Typography>
        </Box>
    );


    const purchasableColumns = [
        { field: "part_name", headerName: t('TABLE_HEADERS.PART_NAME'), flex: 2, renderCell: renderPartName },
        { field: "part_number", headerName: t('TITLES.PART_NUMBER_1', { defaultValue: "Part Number" }), flex: 1.5 },
        {
            field: "part_price",
            headerName: t('TABLE_HEADERS.PRICE'),
            flex: 1,
            align: "center",
            headerAlign: "center",
            valueFormatter: (value) => formatCurrency(value, selectedSiteCurrency)
        },
        {
            field: "quantity",
            headerName: t('TABLE_HEADERS.QUANTITY'),
            flex: 1,
            align: "center",
            headerAlign: "center",
            renderCell: renderQuantity
        },
        {
            field: "sub_total",
            headerName: t('TABLE_HEADERS.SUBTOTAL'),
            flex: 1,
            align: "center",
            headerAlign: "center",
            valueFormatter: (value) => formatCurrency(value, selectedSiteCurrency)
        },
    ];

    const nonPurchasableColumns = [
        { field: "part_name", headerName: t('TABLE_HEADERS.PART_NAME'), flex: 2, renderCell: renderPartName },
        { field: "part_number", headerName: t('TITLES.PART_NUMBER_1', { defaultValue: "Part Number" }), flex: 1.5 },
        {
            field: "quantity",
            headerName: t('TABLE_HEADERS.QUANTITY'),
            flex: 1,
            align: "center",
            headerAlign: "center",
            renderCell: renderQuantity
        },
    ];

    const notAvailableColumns = [
        { field: "part_number", headerName: t('TITLES.PART_NUMBER_1', { defaultValue: "Part Number" }), flex: 1.5 },
        {
            field: "quantity",
            headerName: t('TABLE_HEADERS.QUANTITY'),
            flex: 1,
            align: "center",
            headerAlign: "center",
            renderCell: renderQuantity
        },
    ];


    return (
        <Card sx={{ boxShadow: "0 0 6px 1px rgba(0,0,0,0.1)", borderRadius: "10px", my: 2 }}>
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', columnGap: 2, mb: 2 }}>

                    <Box>
                        <Box>
                            <Typography variant="body1" fontWeight={550}>
                                {t('UI_TEXT.ADD_PARTS_TO_CART')}
                            </Typography>
                            <Typography color="text.secondary">
                                {t('UI_TEXT.YOU_CAN_ADD_PARTS_TO_CART_USING_A_CSVEXCEL_FILE_WITH_PART_AND_QTY')}
                            </Typography>
                        </Box>
                        <div
                            style={{
                                borderBottom: "4px",
                                borderColor: "var(--primary-main) !important",
                                background: "var(--primary-main) !important",
                                backgroundColor: "var(--primary-main) !important",
                                borderBottomColor: "var(--primary-main) !important",
                                height: "2px",
                                margin: "2px 0",
                                width: "90px",

                            }}
                        ></div>
                    </Box>
                    <Button
                        variant="text"
                        color="primary"
                        size="small"
                        startIcon={<Download />}
                        onClick={() => {
                            window.open(`${ASSETS_BASE_URL}/Templates/partsOrderTemplate.csv`, "_blank");
                        }}
                    >
                        {t('TITLES.PARTS_ORDER_TEMPLATE')}
                    </Button>

                </Box>

                <Box sx={{ borderRadius: '10px', background: '#eee', p: 1.5, display: 'flex', justifyContent: 'flex-start', alignItems: 'center', width: '100%', columnGap: 2 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={fileProcessing ? <CircularProgress color="inherit" size={16} /> : <FileUpload />}
                        onClick={handleButtonClick}
                        disabled={fileProcessing}
                    >
                        {fileProcessing
                            ? t('UI_TEXT.PROCESSING', { defaultValue: "Processing..." })
                            : t('UI_TEXT.CHOOSE_FILE')}
                    </Button>
                    <Typography variant="body1" fontSize="11px">
                        {t('UI_TEXT.UPLOAD_A_CSV_OR_EXCEL_FILE_CONTAINING_PART_DETAILS')}
                    </Typography>
                </Box>
                <input
                    type="file"
                    id="fileInput"
                    accept=".csv, .xlsx"
                    style={{ display: "none" }}
                    onChange={handleFileUpload}
                    disabled={fileProcessing}
                />
            </CardContent>

            <ModalWrapper
                open={open}
                setOpen={setOpen}
                width="65vw"
                title={`${t('UI_TEXT.PARTS_SUMMARY')}${fileName ? ` - ${fileName}` : ""}`}
                subtitle={t('UI_TEXT.FOLLOWING_PARTS_WILL_BE_ADDED_TO_CART_OR_REQUEST_LIST')}
                content={
                    <Box sx={{ width: "100%" }}>
                        <Typography
                            variant="body2"
                            color="text.secondary"

                            sx={{ mb: 2, fontStyle: "italic", fontSize: '11px' }}
                        >
                            <b>Note:</b> {t('UI_TEXT.QUANTITIES_CAN_BE_MODIFIED_EITHER_IN_THE_FILE_YOU_SELECTED_OR_ONCE_ITEMS_ARE_ADDED_TO_THE_CART_OR_REQUEST_LIST')}
                        </Typography>
                        {purchasableParts.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="h6" gutterBottom fontWeight={600}>
                                    {t('UI_TEXT.PURCHASABLE_PARTS')}
                                </Typography>
                                <Datagrid
                                    data={purchasableParts}
                                    columns={purchasableColumns}
                                    rowIdField="part_number"
                                    showSearchBar={false}
                                />
                                {/* Totals */}
                                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 4, pr: 2 }}>
                                    <Typography fontWeight={600}>Subtotal:</Typography>
                                    <Typography>
                                        {formatCurrency(
                                            purchasableParts.reduce((sum, item) => sum + Number(item.sub_total || 0), 0),
                                            selectedSiteCurrency
                                        )}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 4, mt: 1, pr: 2 }}>
                                    <Typography variant="h6" color="primary" fontWeight={600}>
                                        {t('UI_TEXT.TOTAL_1')}
                                    </Typography>
                                    <Typography variant="h5" color="primary">
                                        {formatCurrency(
                                            purchasableParts.reduce((sum, item) => sum + Number(item.sub_total || 0), 0),
                                            selectedSiteCurrency
                                        )}
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        {nonPurchasableParts.length > 0 && (
                            <>
                                <Divider sx={{ my: 2 }} />
                                <Typography variant="h6" gutterBottom fontWeight={600}>
                                    {t('UI_TEXT.NON_PURCHASABLE_PARTS')}
                                </Typography>
                                <Datagrid
                                    data={nonPurchasableParts}
                                    columns={nonPurchasableColumns}
                                    rowIdField="part_number"
                                    showSearchBar={false}
                                />
                            </>
                        )}

                        {notAvailableParts.length > 0 && (
                            <>
                                <Divider sx={{ my: 2 }} />
                                <Typography variant="h6" gutterBottom fontWeight={600}>
                                    Not Available Parts
                                </Typography>
                                <Datagrid
                                    data={notAvailableParts}
                                    columns={notAvailableColumns}
                                    rowIdField="part_number"
                                    showSearchBar={false}
                                />
                            </>
                        )}

                        {!purchasableParts.length &&
                            !nonPurchasableParts.length &&
                            !notAvailableParts.length && (
                                <Typography color="text.secondary" align="center">
                                    No valid parts found in the uploaded file.
                                </Typography>
                            )}

                        <Typography
                            variant="body2"
                            color="text.secondary"

                            sx={{ mb: 2, fontStyle: "italic", fontSize: '11px' }}
                        >
                            <b>Note:</b> Some of the selected parts need up-to-date pricing. Please add these to your customer care request list and notify the customer care.
                        </Typography>
                    </Box>
                }
                actions={
                    <>
                        <Button variant="outlined" color="secondary" onClick={handleCancel}>
                            {t('BUTTON.CANCEL')}
                        </Button>
                        <Button variant="contained" color="primary" onClick={handleUploadParts}>
                            Upload Parts
                        </Button>
                    </>
                }
            />
        </Card>
    );
};

export default CartUpload;
